'use client';

import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { useDialogPortalContainer } from '../TovDialogPortalContext/TovDialogPortalContext';
import { cn } from '../../lib/utils';

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  const portalContainer = useDialogPortalContainer();

  return (
    <PopoverPrimitive.Portal container={portalContainer ?? undefined}>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'tov:z-50 tov:w-72 tov:max-w-[calc(100vw-1rem)] tov:origin-(--radix-popover-content-transform-origin) tov:rounded-md tov:border tov:bg-popover tov:p-4 tov:text-popover-foreground tov:shadow-md tov:outline-hidden tov:data-[side=bottom]:slide-in-from-top-2 tov:data-[side=left]:slide-in-from-right-2 tov:data-[side=right]:slide-in-from-left-2 tov:data-[side=top]:slide-in-from-bottom-2 tov:data-[state=closed]:animate-out tov:data-[state=closed]:fade-out-0 tov:data-[state=closed]:zoom-out-95 tov:data-[state=open]:animate-in tov:data-[state=open]:fade-in-0 tov:data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

function PopoverHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="popover-header"
      className={cn('tov:flex tov:flex-col tov:gap-1 tov:text-sm', className)}
      {...props}
    />
  );
}

function PopoverTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return (
    <div
      data-slot="popover-title"
      className={cn('tov:font-medium', className)}
      {...props}
    />
  );
}

function PopoverDescription({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="popover-description"
      className={cn('tov:text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
  PopoverHeader,
  PopoverTitle,
  PopoverDescription,
};
